import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { ShieldCheck, Heart, Award, Eye, Truck, HardHat, Users, Wrench } from "lucide-react";

const valueIcons = [ShieldCheck, Award, Heart, Eye];

export default function About() {
  const { t } = useLang();
  const values = t("about.values");

  return (
    <div data-testid="about-page">
      <SEO
        title={`About ${COMPANY.brandFull} — Integrated FM Partner UAE`}
        description="Orion FM unifies hard FM, soft FM, cleaning and manpower under one SLA-driven contract. Mission, vision and SPIA values."
      />

      {/* HERO — dark cinematic to match Home */}
      <section className="relative overflow-hidden bg-[#050A15] text-white">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1518684079-3c830dcef090?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400"
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-center opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A15]/92 via-[#050A15]/55 to-[#050A15]/20" />
          <div className="absolute -top-40 -right-32 w-[420px] h-[420px] rounded-full bg-blue-600/15 blur-[140px]" />
          <div className="absolute -bottom-40 -left-32 w-[360px] h-[360px] rounded-full bg-amber-500/10 blur-[120px]" />
        </div>
        <div className="container-orion relative py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                {t("about.sectionLabel")}
              </div>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-medium leading-[1.02] text-white">
                {t("about.title")}
              </h1>
              <p className="mt-6 font-heading text-lg sm:text-xl text-blue-300 italic">
                {COMPANY.tagline}
              </p>
            </div>
            <div className="lg:col-span-4 lg:pb-3">
              <p className="text-slate-300/90 leading-relaxed">{t("about.story")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand banner */}
      <section className="relative -mt-16 lg:-mt-20 pb-12">
        <div className="container-orion">
          <div className="relative aspect-[16/7] sm:aspect-[16/6] rounded-3xl overflow-hidden border-2 border-white/10 bg-slate-950 shadow-2xl shadow-black/50">
            <img
              src={COMPANY.banner}
              alt="Orion Facilities Management — Your Property. Professionally Managed."
              className="w-full h-full object-cover"
            />
          </div>
          {/* ISO trust ribbon */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { code: "ISO 9001:2015", label: "Quality Management" },
              { code: "ISO 14001", label: "Environmental Management" },
              { code: "ISO 45001", label: "Occupational Health & Safety" },
            ].map((iso) => (
              <div key={iso.code} className="bg-slate-950 text-white rounded-2xl p-5 border border-slate-800 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/15 text-blue-300 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading text-sm font-semibold text-white leading-tight">{iso.code}</div>
                  <div className="mt-0.5 text-[11px] text-slate-400 leading-snug">{iso.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
          <div className="bg-white p-10 lg:p-14">
            <div className="text-eyebrow text-blue-700">{t("about.missionLabel")}</div>
            <p className="mt-5 font-heading text-2xl lg:text-3xl text-slate-900 tracking-tight font-medium leading-snug">
              {t("about.mission")}
            </p>
          </div>
          <div className="bg-slate-950 text-white p-10 lg:p-14">
            <div className="text-eyebrow text-blue-400">{t("about.visionLabel")}</div>
            <p className="mt-5 font-heading text-2xl lg:text-3xl tracking-tight font-medium leading-snug">
              {t("about.vision")}
            </p>
          </div>
        </div>
      </section>

      {/* OUR FLEET & TEAM — capability proof */}
      <section
        className="py-24 lg:py-32 bg-[#050A15] text-white relative overflow-hidden"
        data-testid="about-fleet-team"
      >
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-blue-500/10 blur-[120px]" />

        <div className="container-orion relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* LEFT — headline + stats */}
            <div className="lg:col-span-5">
              <div className="text-eyebrow text-blue-300">Our Fleet & Team</div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium leading-[1.05]">
                Owned equipment. In-house crews. HSE-first delivery.
              </h2>
              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-lg">
                We don't broker the work. Our teams roll up in Orion vests with our own heavy equipment,
                trained supervisors and DM-approved chemicals — so quality and timelines stay under our control.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-4">
                {[
                  { icon: Truck, v: "12+", l: "Owned assets" },
                  { icon: Users, v: "150+", l: "Trained staff" },
                  { icon: HardHat, v: "100%", l: "PPE-compliant" },
                  { icon: Wrench, v: "24/7", l: "Emergency crew" },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={i}
                      data-testid={`fleet-stat-${i}`}
                      className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm hover:border-blue-400/40 hover:bg-white/[0.07] transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-500/15 text-blue-300 flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="mt-4 font-heading text-3xl font-medium text-white tracking-tight">
                        {s.v}
                      </div>
                      <div className="mt-1 text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">
                        {s.l}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {[
                  "CAT & Bobcat heavy equipment",
                  "HSE certified supervisors",
                  "DM & Civil Defence approved",
                  "MEP & fire specialists in-house",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-300 bg-white/5 border border-white/10 rounded-full px-3 py-1.5"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT — asymmetric photo grid */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-6 grid-rows-6 gap-3 sm:gap-4 h-[520px] sm:h-[600px]">
                {/* CAT skid-steer — hero image */}
                <div className="col-span-4 row-span-4 relative rounded-3xl overflow-hidden group border border-white/10 shadow-2xl shadow-blue-950/50">
                  <img
                    src="https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/lkbu36gp_WhatsApp%20Image%202026-08-26%20at%2010.11.25%20AM.jpeg"
                    alt="Orion CAT 216B3 skid-steer loader at Al Bahar site"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                  <div className="absolute top-5 left-5">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-blue-600 rounded-full px-3 py-1.5">
                      Owned fleet
                    </span>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="font-heading text-xl sm:text-2xl font-medium leading-tight text-white">
                      CAT 216B3 Skid-Steer
                    </div>
                    <div className="mt-1.5 text-xs uppercase tracking-[0.15em] text-blue-200/90 font-semibold">
                      Landscaping · Site prep · Waste haul
                    </div>
                  </div>
                </div>

                {/* Team on site */}
                <div className="col-span-2 row-span-4 relative rounded-3xl overflow-hidden group border border-white/10">
                  <img
                    src="https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/uj274oin_WhatsApp%20Image%202026-09-05%20at%208.54.08%20PM.jpeg"
                    alt="Orion team on site with hard hats and safety vests"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-white/15 backdrop-blur-md border border-white/20 rounded-full px-2.5 py-1">
                      HSE crew
                    </span>
                  </div>
                  <div className="absolute bottom-5 left-4 right-4">
                    <div className="font-heading text-base sm:text-lg font-medium leading-tight text-white">
                      Site supervisors
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-blue-200/90 font-semibold">
                      Yellow vests · Hard hats
                    </div>
                  </div>
                </div>

                {/* Pool renovation team */}
                <div className="col-span-6 row-span-2 relative rounded-3xl overflow-hidden group border border-white/10">
                  <img
                    src="https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/ro6meixl_WhatsApp%20Image%202026-09-05%20at%208.51.12%20PM%20%281%29.jpeg"
                    alt="Orion team performing pool renovation"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/40 to-transparent" />
                  <div className="absolute inset-0 flex items-center px-6 sm:px-8">
                    <div className="max-w-md">
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-blue-600 rounded-full px-3 py-1.5">
                        In-house specialists
                      </span>
                      <div className="mt-3 font-heading text-lg sm:text-xl font-medium leading-tight text-white">
                        Pool refurbishment crew — Ajman FZ
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow">{t("about.valuesLabel")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              SPIA — the four principles behind every shift.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => {
              const Icon = valueIcons[i] || ShieldCheck;
              return (
                <div
                  key={i}
                  data-testid={`value-card-${i}`}
                  className="bg-white border border-slate-200 rounded-2xl p-7 card-hover"
                >
                  <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
